import type { CaptureResult } from "posthog-js";
import { normalizeLang, type Lang } from "@/i18n/locale";
import { PROGRESSIVE_SPEECH_ERROR_CODES } from "../../supabase/functions/_shared/progressiveSpeechProtocol";

export const MIRROR_PROGRESSIVE_SPEECH_EVENT = "mirror_progressive_speech_latency";
export const MIRROR_PROGRESSIVE_SPEECH_KEYS = [
  "schema_version", "cohort", "platform", "app_version", "tier", "language", "network", "synthetic",
  "first_clip_cache", "terminal", "terminal_reason",
  "admission_to_first_complete_clip_ms", "admission_to_html_playing_ms",
  "admission_to_sse_done_ms", "admission_to_last_ended_ms",
  "first_complete_clip_to_html_playing_ms", "total_ms",
] as const;
export type ProgressiveTerminal = "complete" | "stop" | "error";
export type ProgressiveReason = "ended" | "stop" | "superseded" | "unmount"
  | typeof PROGRESSIVE_SPEECH_ERROR_CODES[number] | "protocol" | "playback" | "audio_error" | "cancelled";
export type MirrorProgressiveSpeechObservation =
  | { stage: "admitted" } | { stage: "accepted" } | { stage: "legacy_fallback" }
  | { stage: "first_clip_ready"; cached: boolean | null }
  | { stage: "playing" } | { stage: "stream_done" } | { stage: "ended" }
  | { stage: "error"; code: ProgressiveReason }
  | { stage: "cancelled"; code: "stop" | "superseded" | "unmount" | "owner_changed" };
export type MirrorProgressiveSpeechObserver = (event: MirrorProgressiveSpeechObservation) => void;
export interface MirrorProgressiveSpeechReport {
  readonly schema_version: 2; readonly cohort: "complete_clips_sse";
  readonly platform: "web" | "ios" | "android" | "unknown";
  readonly app_version: string; readonly tier: "free" | "premium" | "unknown";
  readonly language: Lang | "unknown";
  readonly network: "wifi" | "cellular" | "ethernet" | "offline" | "unknown";
  readonly synthetic: boolean; readonly first_clip_cache: "hit" | "miss" | "unknown";
  readonly terminal: ProgressiveTerminal; readonly terminal_reason: ProgressiveReason;
  readonly admission_to_first_complete_clip_ms: number | null;
  readonly admission_to_html_playing_ms: number | null;
  readonly admission_to_sse_done_ms: number | null;
  readonly admission_to_last_ended_ms: number | null;
  readonly first_complete_clip_to_html_playing_ms: number | null;
  readonly total_ms: number | null;
}
export interface ProgressiveSpeechMetadata {
  platform?: unknown; app_version?: unknown; tier?: unknown; language?: unknown; network?: unknown; synthetic?: unknown;
}
const platforms = ["web", "ios", "android", "unknown"], tiers = ["free", "premium", "unknown"];
const networks = ["wifi", "cellular", "ethernet", "offline", "unknown"];
const terminals = ["complete", "stop", "error"];
const reasons = ["ended", "stop", "superseded", "unmount", ...PROGRESSIVE_SPEECH_ERROR_CODES, "protocol", "playback", "audio_error", "cancelled"];
const member = (values: readonly string[], value: unknown): value is string => typeof value === "string" && values.includes(value);
const version = (value: unknown): value is string => value === "unknown" || typeof value === "string" && /^[0-9a-f]{7,40}$/.test(value);
const duration = (value: unknown): value is number | null => value === null || typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 600_000;
export function validateMirrorProgressiveSpeechReport(value: unknown): MirrorProgressiveSpeechReport | null {
  try {
    if (!value || typeof value !== "object" || Array.isArray(value)) return null;
    const row = value as Record<string, unknown>;
    if (Object.keys(row).length !== MIRROR_PROGRESSIVE_SPEECH_KEYS.length
      || !MIRROR_PROGRESSIVE_SPEECH_KEYS.every(key => Object.prototype.hasOwnProperty.call(row, key))
      || row.schema_version !== 2 || row.cohort !== "complete_clips_sse"
      || !member(platforms, row.platform) || !version(row.app_version) || !member(tiers, row.tier)
      || !(row.language === "unknown" || typeof row.language === "string" && normalizeLang(row.language) === row.language)
      || !member(networks, row.network) || typeof row.synthetic !== "boolean"
      || !member(["hit", "miss", "unknown"], row.first_clip_cache)
      || !member(terminals, row.terminal) || !member(reasons, row.terminal_reason)
      || row.terminal === "complete" && row.terminal_reason !== "ended"
      || row.terminal === "stop" && !member(["stop", "superseded", "unmount"], row.terminal_reason)
      || row.terminal === "error" && member(["ended", "stop", "superseded", "unmount"], row.terminal_reason)
      || !MIRROR_PROGRESSIVE_SPEECH_KEYS.filter(key => key.endsWith("_ms")).every(key => duration(row[key]))) return null;
    return Object.freeze({ ...row }) as unknown as MirrorProgressiveSpeechReport;
  } catch { return null; }
}
export function sanitizeMirrorProgressiveSpeechTelemetry(event: CaptureResult | null, publicToken: string | undefined): CaptureResult | null {
  if (!event || event.event !== MIRROR_PROGRESSIVE_SPEECH_EVENT) return event;
  try {
    if (!publicToken || !event.properties) return null;
    const row: Record<string, unknown> = {};
    for (const key of MIRROR_PROGRESSIVE_SPEECH_KEYS) row[key] = event.properties[key];
    const report = validateMirrorProgressiveSpeechReport(row);
    if (!report) return null;
    const distinctId = crypto.randomUUID(), uuid = crypto.randomUUID();
    const valid = (id: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
    if (!valid(distinctId) || !valid(uuid)) return null;
    return { event: MIRROR_PROGRESSIVE_SPEECH_EVENT, uuid, timestamp: new Date(), properties: {
      ...report, token: publicToken, distinct_id: distinctId,
      $process_person_profile: false, $ip: "0.0.0.0", $geoip_disable: true,
    } };
  } catch { return null; }
}
export interface MirrorProgressiveSpeechMetrics {
  readonly observer: MirrorProgressiveSpeechObserver;
  finish(terminal: ProgressiveTerminal, reason: ProgressiveReason): void;
  discard(): void;
}
export function createMirrorProgressiveSpeechMetrics(options: {
  metadata: ProgressiveSpeechMetadata; isCurrent: () => boolean;
  onResult: (report: MirrorProgressiveSpeechReport) => void; now?: () => number;
}): MirrorProgressiveSpeechMetrics {
  let closed = false, admitted = false, accepted = false, start: number | null = null, last: number | null = null;
  let cache: MirrorProgressiveSpeechReport["first_clip_cache"] = "unknown";
  const points: Partial<Record<"ready" | "playing" | "stream" | "ended", number | null>> = {};
  const clock = () => {
    try { const at = (options.now ?? (() => performance.now()))(); if (!Number.isFinite(at) || at < 0 || last !== null && at < last) return null; last = at; return at; }
    catch { return null; }
  };
  const current = () => {
    if (closed) return false;
    try { if (options.isCurrent()) return true; } catch { /* Revoked origin. */ }
    closed = true; return false;
  };
  const elapsed = (a: number | null | undefined, b: number | null | undefined) => a == null || b == null || !duration(b - a) ? null : b - a;
  const point = (key: keyof typeof points) => { if (!Object.prototype.hasOwnProperty.call(points, key)) points[key] = clock(); };
  const finish = (terminal: ProgressiveTerminal, reason: ProgressiveReason) => {
    if (!current()) return;
    if (!accepted) { closed = true; return; }
    if (terminal === "complete" && (!Object.prototype.hasOwnProperty.call(points, "ready") || !Object.prototype.hasOwnProperty.call(points, "stream") || !Object.prototype.hasOwnProperty.call(points, "ended"))) { closed = true; return; }
    const raw = options.metadata, at = clock();
    const report = validateMirrorProgressiveSpeechReport({
      schema_version: 2, cohort: "complete_clips_sse",
      platform: member(platforms, raw.platform) ? raw.platform : "unknown",
      app_version: version(raw.app_version) ? raw.app_version : "unknown",
      tier: member(tiers, raw.tier) ? raw.tier : "unknown",
      language: typeof raw.language === "string" ? normalizeLang(raw.language) ?? "unknown" : "unknown",
      network: member(networks, raw.network) ? raw.network : "unknown", synthetic: raw.synthetic === true,
      first_clip_cache: cache, terminal, terminal_reason: reason,
      admission_to_first_complete_clip_ms: elapsed(start, points.ready),
      admission_to_html_playing_ms: elapsed(start, points.playing),
      admission_to_sse_done_ms: elapsed(start, points.stream),
      admission_to_last_ended_ms: elapsed(start, points.ended),
      first_complete_clip_to_html_playing_ms: elapsed(points.ready, points.playing),
      total_ms: elapsed(start, at),
    });
    closed = true;
    if (report) try { void Promise.resolve(options.onResult(report)).catch(() => {}); } catch { /* Optional telemetry. */ }
  };
  return {
    observer(event) {
      if (!current()) return;
      if (event.stage === "admitted") { if (!admitted) { admitted = true; start = clock(); } return; }
      if (event.stage === "legacy_fallback") { closed = true; return; }
      if (event.stage === "accepted") { if (admitted) accepted = true; return; }
      if (!accepted) return;
      switch (event.stage) {
        case "first_clip_ready":
          if (!Object.prototype.hasOwnProperty.call(points, "ready")) { cache = event.cached === true ? "hit" : event.cached === false ? "miss" : "unknown"; point("ready"); }
          break;
        case "playing": if (Object.prototype.hasOwnProperty.call(points, "ready")) point("playing"); break;
        case "stream_done": if (Object.prototype.hasOwnProperty.call(points, "ready")) point("stream"); break;
        case "ended": point("ended"); finish("complete", "ended"); break;
        case "error": finish("error", event.code); break;
        case "cancelled": if (event.code === "owner_changed") closed = true; else finish("stop", event.code); break;
      }
    }, finish, discard() { closed = true; },
  };
}
export interface MirrorProgressiveSpeechCoordinator {
  configure(metadata: ProgressiveSpeechMetadata): void;
  begin(isCurrent: () => boolean): MirrorProgressiveSpeechMetrics;
  finishCurrent(terminal: ProgressiveTerminal, reason: ProgressiveReason): void;
  discard(): void;
}
export function createMirrorProgressiveSpeechCoordinator(options: { onResult: (report: MirrorProgressiveSpeechReport) => void; now?: () => number }): MirrorProgressiveSpeechCoordinator {
  let metadata: ProgressiveSpeechMetadata = {}, current: MirrorProgressiveSpeechMetrics | null = null, revision = 0;
  return {
    configure(value) { metadata = { ...value }; },
    begin(isCurrent) {
      current?.finish("stop", "superseded"); current?.discard();
      const generation = ++revision;
      current = createMirrorProgressiveSpeechMetrics({ metadata: { ...metadata }, isCurrent: () => generation === revision && isCurrent(), onResult: options.onResult, now: options.now });
      return current;
    },
    finishCurrent(terminal, reason) { current?.finish(terminal, reason); },
    discard() { ++revision; current?.discard(); current = null; },
  };
}
